import React, { useEffect, useState } from 'react';
import { usePlatform } from '../../contexts/PlatformContext';
import { useAuth } from '../../contexts/AuthContext';
import { setExecutionBackend, createAgentConnectorAssignment } from '../../services/api';
import ExteletConnectorSkills from '../v2/extelet/ExteletConnectorSkills';
import { EYEBROW, NOTE } from './typeScale';

/**
 * What it can actually do, asked where an ExteLet gets it.
 *
 * <h3>Why this step exists</h3>
 *
 * <p>An ExteLet built end to end through Protect finished with no skills at all.
 * Nothing in the walk ever gave it any: the two service functions written for a
 * capabilities step had no callers anywhere, and the script had no step. Its
 * intent binding then froze nothing, and the Intent step said so — "0 skills
 * frozen at bind time. It allows no skill, so every call that declares this
 * intent is refused." Every governed call such an agent makes is refused for
 * having no capability, which is not a protection decision and proves nothing.</p>
 *
 * <h3>Why it sits in Promote</h3>
 *
 * <p>Because that is where an ExteLet's capability comes from. It runs on a live
 * connector, and that connector's advertised actions become its governed skills;
 * the rules in force follow from those, which is why the readiness gate's
 * `governance_materialized` is a Promote check and not an Intent one.</p>
 *
 * <h3>Why it reuses the v2 picker rather than drawing its own</h3>
 *
 * <p>{@code ExteletConnectorSkills} already does exactly this on the platform's
 * Build surface — reads the connector's actions from the shared marketplace
 * catalogue, adds them through the same skills endpoints, starts destructive
 * actions unticked. A second picker here would be a second path to the same
 * answer, and the first keyboard fix would land in only one of them. It is
 * rendered inside `p7-v2-bridge`, which re-binds the v2 colour names to the p7
 * ones for its subtree only, so it carries Protect's palette without a fork.</p>
 */
export default function ConnectorSkillsAsk({
  agentId, state, onRefresh, onAnswer, busy = false,
}) {
  const platform = usePlatform();
  const { session } = useAuth();
  const connectors = Array.isArray(platform?.connectors) ? platform.connectors : [];
  const loadConnectors = platform?.loadConnectors;
  const orgId = session?.orgId;
  const tenantId = session?.tenantId;

  /* The context owns the tenant's connectors and exposes the loader, but nothing
     calls it on its own — each dashboard surface asks for what it needs, and the
     wizard renders outside that shell. So on this route the list was empty, the
     connector this agent is already attached to could not be resolved, and the
     step drew nothing at all. Asking the shared loader keeps this off a read of
     its own, and the scope is the signed-in org and tenant, never a blanket
     fetch. */
  useEffect(() => {
    if (connectors.length > 0 || typeof loadConnectors !== 'function') return;
    if (!orgId && !tenantId) return;
    loadConnectors({ orgId, tenantId });
  }, [connectors.length, loadConnectors, orgId, tenantId]);

  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState(null);
  const [choice, setChoice] = useState('');

  const backend = state?.backend ?? null;
  const mapped = state?.mapped ?? 0;
  const mapError = state?.mapError ?? null;
  const assignments = Array.isArray(state?.assignments) ? state.assignments : [];
  const skillCount = state?.skillCount ?? 0;
  const onLiveConnector = backend === 'connector';

  const act = async (fn, failure) => {
    setWorking(true);
    setProblem(null);
    try {
      const res = await fn();
      if (res && res.ok === false) {
        setProblem(res.error || res.message || failure);
        return;
      }
      await onRefresh?.();
    } catch (e) {
      setProblem(e?.message || failure);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div style={COL}>
      {/* Where its tools come from. Sample data is a real answer, not a missing
          one — it is how somebody tries the shape of a thing before wiring it to
          a system of record — but it is not a capability the gate can protect. */}
      {!onLiveConnector ? (
        <div style={PANEL}>
          <span style={EYEBROW}>How its tool calls run</span>
          <p style={{ ...NOTE, marginTop: '7px' }}>
            It is set to return sample results. Nothing it does reaches a real system,
            so there is nothing yet for a rule to hold back. Point it at a live
            connector and its tools are taken from what that connector provides.
          </p>
          <button type="button" className="p7-btn p7-btn--sm" style={{ marginTop: '8px' }}
                  disabled={busy || working || !agentId}
                  onClick={() => act(() => setExecutionBackend(agentId, 'connector'),
                    'Could not switch it to a live connector.')}>
            {working ? 'Switching…' : 'Use a live connector'}
          </button>
        </div>
      ) : null}

      {onLiveConnector && assignments.length === 0 ? (
        <div style={PANEL}>
          <span style={EYEBROW}>Connect a data connector</span>
          <p style={{ ...NOTE, marginTop: '7px' }}>
            Its tools are derived from what the connector provides — there is no
            per-tool setup. Connectors are configured once for the tenant.
          </p>
          {connectors.length === 0 ? (
            <p style={{ ...NOTE, marginTop: '7px' }}>
              No connectors are configured for this tenant yet, so there is nothing to
              attach. One has to be added on the Connectors page before this step can
              give the agent anything.
            </p>
          ) : (
            <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <select className="p7-input" value={choice} aria-label="Connector"
                      onChange={(e) => setChoice(e.target.value)}
                      style={{ minWidth: '240px' }}>
                <option value="">Choose a connector…</option>
                {connectors.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.type ? ` · ${c.type}` : ''}
                  </option>
                ))}
              </select>
              <button type="button" className="p7-btn p7-btn--sm"
                      disabled={busy || working || !choice}
                      onClick={() => act(
                        () => createAgentConnectorAssignment(agentId, { connectorId: choice }),
                        'Could not connect that connector.')}>
                {working ? 'Connecting…' : 'Connect'}
              </button>
            </div>
          )}
        </div>
      ) : null}

      {/* Attached, but the connector list has not arrived. Saying so beats an
          empty panel: the agent IS wired to something, and a step that shows
          nothing reads as a step with nothing to offer. */}
      {onLiveConnector && assignments.length > 0 && connectors.length === 0 ? (
        <div style={PANEL}>
          <span style={EYEBROW}>Its connector</span>
          <p style={{ ...NOTE, marginTop: '7px' }}>
            It is attached to a connector, but the connector list could not be read
            just now, so its actions cannot be shown. Nothing has changed about the
            agent — reopening this step will try again.
          </p>
        </div>
      ) : null}

      {/* The picker itself, unchanged, one per attached connector. */}
      {onLiveConnector && assignments.map((a) => {
        const c = connectors.find((cc) => cc.id === a.connectorId);
        if (!c) return null;
        return (
          <div key={a.id} className="p7-v2-bridge" style={PANEL}>
            <ExteletConnectorSkills
              connector={c}
              agent={{ id: agentId }}
              addToast={() => {}}
              onChanged={onRefresh}
            />
          </div>
        );
      })}

      {problem ? (
        <p style={{ ...NOTE, color: 'var(--p7-deny-ink)' }}>{problem}</p>
      ) : null}

      {/* What the count means for the step before this one. The binding freezes
          the skill list at bind time, so skills added now are outside it until
          it is bound again — the Intent step says the same thing from its side,
          and somebody who adds skills here and stops would be left with an agent
          whose every call is refused. */}
      {/* The half an operator cannot see and must not have to think about: a
          connector skill is a tool the agent may call, and protection binds to
          a mapped capability instead. Adding the first without the second is
          what made materialising policy answer 400 with three skills on file.
          It is kept in step automatically; this says so, and says it plainly
          when the platform refused. */}
      {mapError ? (
        <p style={{ ...NOTE, color: 'var(--p7-deny-ink)' }}>
          Its skills are on file, but registering them as protected capabilities was
          refused: {mapError} Until that succeeds, no rule can be put in force on it.
        </p>
      ) : null}

      <p style={NOTE}>
        {skillCount === 0
          ? 'It holds no protected skills yet. An intent bound now would freeze an empty '
            + 'list, and every call declaring it would be refused.'
          : `${skillCount} protected skill${skillCount === 1 ? '' : 's'} on file`
            + `${mapped ? ` and registered as ${mapped} protected capabilit${mapped === 1 ? 'y' : 'ies'}` : ''}. `
            + 'If its intent was bound before these existed, bind it again so they are covered.'}
      </p>

      <div>
        <button type="button" className="p7-btn p7-btn--primary" disabled={busy || working}
                onClick={() => onAnswer({ skillCount, backend })}>
          Continue
        </button>
      </div>
    </div>
  );
}

const COL = { display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' };
const PANEL = {
  border: '1px solid var(--p7-line)', borderRadius: 'var(--p7-radius-control)',
  padding: '11px 13px', background: 'var(--p7-surface-sunken)',
};
